import { Navigate, Route, Routes, BrowserRouter } from "react-router-dom";
import { AuthProvider, useAuth } from "./AuthContext";
import { ToastProvider } from "./Toast";
import { AppShell } from "./AppShell";
import { LoginPage } from "@/pages/Login";
import { DashboardPage } from "@/pages/Dashboard";
import { CustomersPage } from "@/pages/Customers";
import { CustomerDetailPage } from "@/pages/CustomerDetail";
import { NewCustomerPage } from "@/pages/NewCustomer";
import { BookingsPage } from "@/pages/Bookings";
import { NewBookingPage } from "@/pages/NewBooking";
import { BookingDetailPage } from "@/pages/BookingDetail";
import { NotificationsPage } from "@/pages/Notifications";
import { WebhooksPage } from "@/pages/Webhooks";
import { AuditPage } from "@/pages/Audit";
import { RecurringPage } from "@/pages/Recurring";
import { PublicBookingPage } from "@/pages/PublicBooking";
import { ManageBookingPage } from "@/pages/ManageBooking";
import { SettingsPage } from "@/pages/Settings";
import { QueuePage } from "@/pages/Queue";
import { WaitlistsPage } from "@/pages/Waitlists";
import { RefundsPage } from "@/pages/Refunds";
import { ReportsPage } from "@/pages/Reports";
import type { ReactElement } from "react";
import { AccountPage } from "@/pages/Account";
import { BillingPage } from "@/pages/Billing";
import { ReconciliationPage } from "@/pages/Reconciliation";
import { PaymentOperationsPage } from "@/pages/PaymentOperations";
import { WashExecutionPage } from "@/pages/WashExecution";
import { WorkforcePage } from "@/pages/Workforce";
import { InventoryPage } from "@/pages/Inventory";
import { CustomerValuePage } from "@/pages/CustomerValue";
import { HelpCenterPage } from "@/pages/HelpCenter";

export function App() {
  return (
    <BrowserRouter>
      <ToastProvider>
        <AuthProvider>
          <Routes>
            <Route path="/book" element={<PublicBookingPage />} />
            <Route path="/help" element={<HelpCenterPage />} />
            <Route path="/help/:slug" element={<HelpCenterPage />} />
            <Route path="/manage/:code" element={<ManageBookingPage />} />
            <Route element={<RequireAuth><AppShell /></RequireAuth>}>
              <Route path="/" element={<DashboardPage />} />
              <Route path="/customers" element={<CustomersPage />} />
              <Route path="/customers/new" element={<NewCustomerPage />} />
              <Route path="/customers/:id" element={<CustomerDetailPage />} />
              <Route path="/bookings" element={<BookingsPage />} />
              <Route path="/bookings/new" element={<NewBookingPage />} />
              <Route path="/bookings/:id" element={<BookingDetailPage />} />
              <Route path="/queue" element={<QueuePage />} />
              <Route path="/waitlists" element={<WaitlistsPage />} />
              <Route path="/settings" element={<SettingsPage />} />
              <Route path="/settings/:tab" element={<SettingsPage />} />
              <Route path="/notifications" element={<NotificationsPage />} />
              <Route path="/webhooks" element={<WebhooksPage />} />
              <Route path="/audit" element={<AuditPage />} />
              <Route path="/recurring" element={<RecurringPage />} />
              <Route path="/refunds" element={<RefundsPage />} />
              <Route path="/reports" element={<ReportsPage />} />
              <Route path="/account" element={<AccountPage />} />
              <Route path="/billing" element={<BillingPage />} />
              <Route path="/reconciliation" element={<ReconciliationPage />} />
              <Route path="/payment-operations" element={<PaymentOperationsPage />} />
              <Route path="/wash-execution/:bookingId" element={<WashExecutionPage />} />
              <Route path="/workforce" element={<WorkforcePage />} />
              <Route path="/inventory" element={<InventoryPage />} />
              <Route path="/customer-value" element={<CustomerValuePage />} />
            </Route>
            <Route path="/login" element={<LoginPage />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </AuthProvider>
      </ToastProvider>
    </BrowserRouter>
  );
}

function RequireAuth({ children }: { children: ReactElement }) {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="grid min-h-svh place-items-center bg-background text-foreground">
        <div className="text-sm text-muted-foreground">Loading…</div>
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  return children;
}
